/**
 * The back office's one date/time module — every timestamp an operator reads,
 * and every date boundary the screens send to an API, is produced here.
 *
 * The whole module is pinned to Asia/Bangkok. Back-office staff work from
 * several countries against one Bangkok ledger, so a row's time has to be the
 * same string on every desk: two people reading the same withdrawal must be
 * able to quote its timestamp to each other without converting anything, and a
 * "today" filter must mean the platform's business day rather than the day the
 * viewer's laptop happens to be in.
 *
 * `Intl.DateTimeFormat` / `toLocale*` are out of bounds (CLAUDE.md §3.9);
 * date-fns + date-fns-tz `formatInTimeZone` is the one technique. The LANGUAGE
 * still follows the viewer (month names), only the ZONE is fixed.
 *
 * `now` is injected into the preset helpers (never `new Date()` here) so they
 * stay pure and testable — see §6.8. Their boundaries resolve the Bangkok civil
 * day of `now` via date-fns-tz, re-anchor it at UTC midnight, then do day-level
 * math with UTC accessors so the result is independent of the runtime timezone.
 */
import { isValid, parseISO } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { enUS, th, km, vi, zhCN, type Locale } from 'date-fns/locale';

const TZ = 'Asia/Bangkok';

/**
 * i18next language tag → date-fns Locale.
 *
 * date-fns ships locales for en/th/km/vi/zh but NOT Lao (lo) or Burmese (my),
 * so those fall back to enUS for month names — the same compromise the player
 * app makes (§3.9). Duration and unit labels never come from here; they are
 * i18n keys, so all seven languages are covered there (see ./duration.ts).
 */
const DATE_FNS_LOCALES: Record<string, Locale> = {
  en: enUS,
  th,
  km,
  vi,
  zh: zhCN,
};

export function dateFnsLocale(lang: string | undefined): Locale {
  const base = (lang ?? 'en').split('-')[0]!;
  return DATE_FNS_LOCALES[base] ?? enUS;
}

function parseTimestamp(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = typeof value === 'string' ? parseISO(normalizeTimestamp(value)) : value;
  return isValid(d) ? d : null;
}

/**
 * The table/detail timestamp: a Bangkok instant in the viewer's language, e.g.
 * "Jun 22, 2026 10:16" in English and "22 มิ.ย. 2026 10:16" in Thai.
 *
 * `PP` is date-fns' localized medium date, so the day/month/year ORDER follows
 * the reader's language while the clock stays on 24h and the zone stays on
 * Bangkok. A bare `HH:mm` rather than `p` for the same reason the backend pins
 * `HH:mm`: an operator quoting a time to a colleague should never have to carry
 * an am/pm marker across a language boundary.
 *
 * Returns `'-'` for a missing or unparseable value rather than an Invalid Date.
 */
export function bangkokShortDateTime(
  value: string | Date | null | undefined,
  opts: { lang?: string; withTime?: boolean } = {},
): string {
  const d = parseTimestamp(value);
  if (!d) return '-';
  const { lang = 'en', withTime = true } = opts;
  return formatInTimeZone(d, TZ, withTime ? 'PP HH:mm' : 'PP', { locale: dateFnsLocale(lang) });
}

// Several services serialize timestamptz in raw Postgres text form
// ("2026-06-11 16:49:30.759312+00"). V8 parses it leniently but WebKit does
// not, so normalize to strict ISO 8601 before handing it to a parser.
// `columns-helper` re-exports this for the table cells that already used it.
const PG_TIMESTAMP = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)([+-]\d{2})(?::?(\d{2}))?$/;

export function normalizeTimestamp(value: string): string {
  const m = PG_TIMESTAMP.exec(value);
  if (!m) return value;
  return `${m[1]}T${m[2]}${m[3]}:${m[4] ?? '00'}`;
}

/**
 * An instant as Bangkok wall-clock date AND time in sortable numeric form,
 * e.g. "2026-08-30 17:00".
 *
 * The same instant and zone as `bangkokShortDateTime`, in a different shape:
 * this one is language-independent, for boundaries an operator has to reason
 * about and compare — a promotion withdrawal ceiling counts withdrawals from
 * an instant, and support comparing it against a withdrawal list wants two
 * strings that sort and diff, not two prose dates.
 *
 * Returns `'-'` for a missing or unparseable value rather than an Invalid Date.
 */
export function bangkokDateTime(iso: string | null | undefined): string {
  const d = parseTimestamp(iso);
  return d ? formatInTimeZone(d, TZ, 'yyyy-MM-dd HH:mm') : '-';
}

/**
 * The Asia/Bangkok civil day an instant falls on, as `YYYY-MM-DD`.
 *
 * The win/loss report endpoint takes a bare `YYYY-MM-DD` and reads it as a
 * Bangkok civil day (`apps/bet/src/repo/bets.ts`, `AT TIME ZONE
 * 'Asia/Bangkok'`), while the report screens hold an ISO instant range. Slicing
 * the instant (`iso.slice(0, 10)`) would hand it the UTC date, which for
 * anything from 17:00Z onwards names the day BEFORE the one on screen — so a
 * drill would silently drop the operator's evening. An unparseable value yields
 * `''`, which callers treat as "no range" rather than forwarding an
 * Invalid Date.
 */
export function bangkokDay(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? formatInTimeZone(d, TZ, 'yyyy-MM-dd') : '';
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * A picked calendar day widened to the ISO instant that day BEGINS in Bangkok.
 *
 * The report endpoints validate `format: 'date-time'`, so a day pair from a
 * picker has to be widened before it goes on the wire — and the widening is
 * where the zone gets decided. `startOfDay(parseISO(day))` uses the machine's
 * midnight, so the same picked day means a different eight hours of ledger to
 * an operator in Bangkok and one in London, and neither total reconciles with
 * the Bangkok daily cash report the numbers are checked against.
 *
 * Returns `null` for anything that is not a well-formed, real `YYYY-MM-DD`;
 * callers read that as "no range" rather than putting an Invalid Date on the
 * wire. Note `2026-02-31` is well SHAPED and not real — `fromZonedTime` would
 * roll it silently into March — so the round trip back through the formatter
 * is what rejects it.
 */
export function bangkokDayStartIso(day: string): string | null {
  if (!DAY.test(day)) return null;
  const at = fromZonedTime(`${day}T00:00:00.000`, TZ);
  if (!isValid(at) || formatInTimeZone(at, TZ, 'yyyy-MM-dd') !== day) return null;
  return at.toISOString();
}

/** The instant a picked calendar day ENDS in Bangkok, inclusive to the ms. */
export function bangkokDayEndIso(day: string): string | null {
  if (!DAY.test(day)) return null;
  const at = fromZonedTime(`${day}T23:59:59.999`, TZ);
  if (!isValid(at) || formatInTimeZone(at, TZ, 'yyyy-MM-dd') !== day) return null;
  return at.toISOString();
}

/**
 * An instant as the `YYYY-MM-DDTHH:mm` an `<input type="datetime-local">`
 * wants, on the Bangkok clock.
 *
 * The input element has no timezone: it shows and returns a bare wall-clock
 * string, and whoever fills it decides which clock that is. Feeding it
 * `iso.slice(0, 16)` shows the UTC wall clock while the browser hands the typed
 * value back as the machine's — so a range the operator never edited moved by
 * their offset the moment they touched either field. Both directions are
 * Bangkok here, which is the only way the field round-trips.
 */
export function bangkokDateTimeInput(iso: string | Date | null | undefined): string {
  const d = parseTimestamp(iso);
  return d ? formatInTimeZone(d, TZ, "yyyy-MM-dd'T'HH:mm") : '';
}

/**
 * The reverse of `bangkokDateTimeInput`: a bare wall-clock string from a
 * `datetime-local` input, read AS Bangkok, as the ISO instant it names.
 *
 * Returns `''` for a cleared or malformed field so a caller can drop it rather
 * than send an Invalid Date.
 */
export function bangkokInputToIso(value: string): string {
  if (!DAY_TIME.test(value)) return '';
  const at = fromZonedTime(value, TZ);
  return isValid(at) ? at.toISOString() : '';
}

/**
 * The same reverse, for a field that names the INCLUSIVE END of a range.
 *
 * A `datetime-local` box has minute precision, and every receiver of these
 * ranges reads the upper bound as inclusive to the millisecond. So the plain
 * conversion above, which fills the seconds with zero, means the moment an
 * operator touched the To box the range lost the final 59.999 seconds of the
 * minute they picked — and picking 23:59, which is what the day presets show,
 * dropped the last minute of the day off a money report.
 *
 * An upper bound typed to the minute means "through the end of that minute":
 * the end of whatever unit the operator actually named. That also makes the
 * round trip stable, which is what keeps an untouched field from moving —
 * 23:59:59.999 displays as 23:59, and 23:59 converts back to 23:59:59.999.
 */
export function bangkokInputToIsoEnd(value: string): string {
  if (!DAY_TIME.test(value)) return '';
  const hasSeconds = value.length > 16;
  const hasMillis = value.includes('.');
  const filled = hasMillis ? value : hasSeconds ? `${value}.999` : `${value}:59.999`;
  const at = fromZonedTime(filled, TZ);
  return isValid(at) ? at.toISOString() : '';
}

/**
 * An instant as `HH:mm` on the Bangkok clock.
 *
 * The shell's header clock and the page watermark both read the platform's
 * business clock rather than the viewer's machine: a remote operator working
 * from another zone has to be able to quote a time a Bangkok colleague can
 * match against a ledger row without converting anything. `now` is injected
 * (§6.8) so the callers stay testable; `toLocaleTimeString` / `Intl` are out
 * of bounds here (§3.9) and `formatInTimeZone` is the one technique.
 */
export function bangkokClockTime(now: Date): string {
  return formatInTimeZone(now, TZ, 'HH:mm');
}

export type PresetKind = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth';

function bangkokCivilDay(now: Date): Date {
  const ymd = formatInTimeZone(now, TZ, 'yyyy-MM-dd');
  return new Date(`${ymd}T00:00:00Z`);
}

function ymd(d: Date): string {
  return formatInTimeZone(d, 'UTC', 'yyyy-MM-dd');
}

function previousDay(day: Date): Date {
  const previous = new Date(day);
  previous.setUTCDate(day.getUTCDate() - 1);
  return previous;
}

function startOfIsoWeek(day: Date): Date {
  // getUTCDay: Sun=0..Sat=6 → days since Monday.
  const offset = (day.getUTCDay() + 6) % 7;
  const monday = new Date(day);
  monday.setUTCDate(day.getUTCDate() - offset);
  return monday;
}

function startOfCalendarMonth(day: Date): Date {
  return new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), 1));
}

export function bangkokPreset(kind: PresetKind, now: Date): { from: string; to: string } {
  const today = bangkokCivilDay(now);
  const to = ymd(today);

  switch (kind) {
    case 'today':
      return { from: to, to };
    case 'yesterday': {
      const day = ymd(previousDay(today));
      return { from: day, to: day };
    }
    case 'thisWeek':
      return { from: ymd(startOfIsoWeek(today)), to };
    case 'thisMonth':
      return { from: ymd(startOfCalendarMonth(today)), to };
  }
}

const MONTH_KEY = /^(\d{4})-(0[1-9]|1[0-2])$/;

/**
 * The Bangkok calendar month an instant falls in, as `YYYY-MM`: the key the
 * art cost page asks svc-art for. `''` for an unparseable value.
 */
export function bangkokMonth(value: string | Date): string {
  const d = parseTimestamp(value);
  return d ? formatInTimeZone(d, TZ, 'yyyy-MM') : '';
}

/**
 * A `YYYY-MM` month as the reader says it, e.g. "October 2026" in English and
 * "ตุลาคม 2026" in Thai. The key is a calendar month, not an instant, so no
 * zone applies; it is read at noon UTC of the 1st so no runtime offset can
 * move it into the month before. A malformed key is returned as it is.
 */
export function formatMonthKey(key: string, lang: string | undefined): string {
  const m = MONTH_KEY.exec(key);
  if (!m) return key;
  return formatInTimeZone(new Date(`${key}-01T12:00:00Z`), 'UTC', 'LLLL yyyy', { locale: dateFnsLocale(lang) });
}
